import React, { useState, useEffect } from "react";

import { UserCircle, Plus, Loader2, Edit2, Trash2 } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import proxyApiService from "../services/proxy-api";

const ProfileSelector = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectingProfile, setSelectingProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [providerAccessStatus, setProviderAccessStatus] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, [playlistId]);

  const fetchAvatars = async () => {
    try {
      setLoadingAvatars(true);
      const response = await proxyApiService.proxyRequest(
        playlistId,
        "/avatars"
      );
      const avatarUrls = response.data || response || [];

      setAvailableAvatars(avatarUrls);

      // Select a random avatar by default
      if (avatarUrls.length > 0) {
        const randomIndex = Math.floor(Math.random() * avatarUrls.length);

        setSelectedAvatar(avatarUrls[randomIndex]);
      }
    } catch (error) {
      console.error("Error fetching avatars:", error);
      // Set some default avatars if the API fails
      const defaultAvatars = Array.from(
        { length: 34 },
        (_, i) =>
          `https://d1hm15kdjuu43j.cloudfront.net/avatars/avatar_${i + 1}.jpg`
      );

      setAvailableAvatars(defaultAvatars);
      setSelectedAvatar(defaultAvatars[0]);
    } finally {
      setLoadingAvatars(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setProviderAccessStatus(null);
      const response = await proxyApiService.getProfiles(playlistId);

      // The API might return the profiles directly or nested in data
      const profilesData =
        response.profiles || response.data?.profiles || response.data || [];

      // Map the API response to the expected format
      const mappedProfiles = Array.isArray(profilesData)
        ? profilesData.map((profile) => ({
            id: profile.profileId || profile.id,
            name: profile.profileName || profile.name,
            avatar: profile.avatarUrl || profile.avatar,
            isKids: profile.isKids || false,
          }))
        : [];

      setProfiles(mappedProfiles);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      if (error.errorType?.startsWith("provider_access_")) {
        setProviderAccessStatus(error.errorType);
      } else {
        toast.error("Failed to load profiles");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelect = async (profile) => {
    try {
      setSelectingProfile(true);
      const response = await proxyApiService.loginProfile(playlistId, {
        profileId: profile.id,
        profileName: profile.name,
      });

      // Check for token in response directly or in data
      const token = response.token || response.data?.token;

      if (token) {
        // Store the profile token for this playlist
        sessionStorage.setItem(`profile_token_${playlistId}`, token);
        sessionStorage.setItem(
          `profile_${playlistId}`,
          JSON.stringify(profile)
        );

        // Navigate to the playlist manager
        navigate(`/playlist-manager/${playlistId}/manage`);
      } else {
        toast.error("No token received from profile login");
      }
    } catch (error) {
      console.error("Error selecting profile:", error);
      toast.error("Failed to select profile");
    } finally {
      setSelectingProfile(false);
    }
  };

  const openAddProfileModal = () => {
    setEditingProfile(null);
    setNewProfileName("");
    setSelectedAvatar("");
    setShowAddProfile(true);
    fetchAvatars();
  };

  const openEditProfileModal = (profile, e) => {
    e.stopPropagation(); // Prevent profile selection
    setEditingProfile(profile);
    setNewProfileName(profile.name);
    setSelectedAvatar(profile.avatar);
    setShowAddProfile(true);
    fetchAvatars();
  };

  const handleSaveProfile = async () => {
    if (!newProfileName.trim()) {
      toast.error("Please enter a profile name");

      return;
    }

    if (!selectedAvatar) {
      toast.error("Please select an avatar");

      return;
    }

    try {
      setAddingProfile(true);

      if (editingProfile) {
        // Update existing profile
        await proxyApiService.updateProfile(playlistId, editingProfile.id, {
          profileName: newProfileName.trim(),
          avatarUrl: selectedAvatar,
        });

        toast.success("Profile updated successfully");
      } else {
        // Check profile limit for new profiles
        if (profiles.length >= 4) {
          toast.error(
            "Maximum of 4 profiles allowed. Please delete an existing profile first."
          );

          return;
        }

        // Create new profile
        await proxyApiService.createProfile(playlistId, {
          profileName: newProfileName.trim(),
          avatarUrl: selectedAvatar,
        });

        toast.success("Profile created successfully");
      }

      setNewProfileName("");
      setSelectedAvatar("");
      setEditingProfile(null);
      setShowAddProfile(false);

      // Refresh profiles list
      setTimeout(() => {
        fetchProfiles();
      }, 500);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error(
        editingProfile ? "Failed to update profile" : "Failed to create profile"
      );
    } finally {
      setAddingProfile(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!editingProfile) return;

    try {
      setDeletingProfile(true);
      await proxyApiService.deleteProfile(playlistId, editingProfile.id);

      toast.success("Profile deleted successfully");
      setShowDeleteConfirm(false);
      setShowAddProfile(false);
      setEditingProfile(null);
      setNewProfileName("");
      setSelectedAvatar("");

      // Refresh profiles list
      setTimeout(() => {
        fetchProfiles();
      }, 500);
    } catch (error) {
      console.error("Error deleting profile:", error);
      toast.error("Failed to delete profile");
    } finally {
      setDeletingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading profiles...</p>
        </div>
      </div>
    );
  }

  if (providerAccessStatus) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{
          backgroundImage: "url(/background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="w-full max-w-xl rounded-2xl bg-gray-900/90 border border-gray-700 p-8 text-center shadow-2xl">
          <h1 className="text-3xl font-bold text-white mb-4">
            Provider Access Unavailable
          </h1>
          <p className="text-gray-300 mb-3">
            {providerAccessStatus === "provider_access_unavailable"
              ? "Your LunaKey is active, but access to this playlist could not be verified with your provider."
              : "Your LunaKey is active, but your provider is no longer accepting the credentials for this playlist."}
          </p>
          <p className="text-gray-400 mb-8">
            {providerAccessStatus === "provider_access_unavailable"
              ? "The provider may be temporarily unavailable. Recheck access, and contact your provider if the problem continues."
              : "Your provider subscription may have expired, been disabled, or had its credentials changed. Contact your provider for more information."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button className="btn btn-primary" onClick={fetchProfiles}>
              Recheck Access
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-start justify-center pt-32"
      style={{
        backgroundImage: "url(/background.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-6xl px-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            Select a Profile
          </h1>
          <p className="text-gray-400">
            Choose a profile to continue to the playlist manager
          </p>
          {profiles.length >= 4 && (
            <p className="text-sm text-yellow-400 mt-2">
              Maximum of 4 profiles reached. Delete an existing profile to add a
              new one.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-4 sm:gap-6">
          {profiles.map((profile) => (
            <div
              className="group relative rounded-full p-3 sm:p-6 transition-all duration-200 hover:scale-105 flex flex-col items-center justify-center"
              key={profile.id}
            >
              <button
                className="w-full flex flex-col items-center space-y-3 sm:space-y-4"
                disabled={selectingProfile}
                onClick={() => handleProfileSelect(profile)}
              >
                <div className="relative">
                  {profile.avatar ? (
                    <img
                      alt={profile.name}
                      className="w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover border-4 border-transparent hover:border-blue-500 transition-all duration-200"
                      src={profile.avatar}
                    />
                  ) : (
                    <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-4 border-transparent hover:border-blue-500 transition-all duration-200">
                      <UserCircle className="w-14 sm:w-16 h-14 sm:h-16 text-white" />
                    </div>
                  )}
                  {/* Edit button */}
                  <button
                    className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 p-1.5 sm:p-2 bg-white hover:bg-gray-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg"
                    title="Edit profile"
                    onClick={(e) => openEditProfileModal(profile, e)}
                  >
                    <Edit2 className="w-3 sm:w-4 h-3 sm:h-4 text-gray-800" />
                  </button>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white">
                  {profile.name}
                </h3>
                {profile.isKids && (
                  <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                    Kids
                  </span>
                )}
              </button>
            </div>
          ))}

          {/* Add Profile Button - Only show if less than 4 profiles */}
          {profiles.length < 4 && (
            <button
              className="group relative rounded-full p-3 sm:p-6 transition-all duration-200 hover:scale-105 flex flex-col items-center justify-center"
              onClick={openAddProfileModal}
            >
              <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-gray-700 flex items-center justify-center group-hover:bg-gray-600 border-4 border-transparent group-hover:border-blue-500 transition-all duration-200">
                  <Plus className="w-16 sm:w-20 h-16 sm:h-20 text-gray-400 group-hover:text-white stroke-[3]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-400 group-hover:text-white">
                  Add Profile
                </h3>
              </div>
            </button>
          )}
        </div>

        {/* Add/Edit Profile Modal */}
        {showAddProfile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-white mb-4">
                {editingProfile ? "Edit Profile" : "Add New Profile"}
              </h2>

              {/* Delete button for editing mode */}
              {editingProfile && (
                <button
                  className="mb-4 text-red-400 hover:text-red-300 flex items-center space-x-2"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Profile</span>
                </button>
              )}

              {/* Profile Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Profile Name
                </label>
                <input
                  autoFocus
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={20}
                  placeholder="Enter profile name"
                  type="text"
                  value={newProfileName}
                  onChange={(e) => setNewProfileName(e.target.value)}
                />
              </div>

              {/* Avatar Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Choose Avatar
                </label>
                {loadingAvatars ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-6 gap-3 max-h-64 overflow-y-auto p-2 bg-gray-900 rounded-lg">
                    {availableAvatars.map((avatar, index) => (
                      <button
                        className={`relative rounded-lg overflow-hidden transition-all ${
                          selectedAvatar === avatar
                            ? "ring-2 ring-blue-500 scale-110"
                            : "hover:scale-105"
                        }`}
                        key={index}
                        onClick={() => setSelectedAvatar(avatar)}
                      >
                        <img
                          alt={`Avatar ${index + 1}`}
                          className="w-full h-full object-cover"
                          src={avatar}
                        />
                        {selectedAvatar === avatar && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                            <div className="bg-white rounded-full p-1">
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  clipRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  fillRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    addingProfile || !newProfileName.trim() || !selectedAvatar
                  }
                  onClick={handleSaveProfile}
                >
                  {addingProfile ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {editingProfile ? "Updating..." : "Creating..."}
                    </span>
                  ) : editingProfile ? (
                    "Update Profile"
                  ) : (
                    "Create Profile"
                  )}
                </button>
                <button
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                  disabled={addingProfile}
                  onClick={() => {
                    setShowAddProfile(false);
                    setNewProfileName("");
                    setSelectedAvatar("");
                    setEditingProfile(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">
                Delete Profile
              </h2>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete the profile "
                {editingProfile?.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                  disabled={deletingProfile}
                  onClick={handleDeleteProfile}
                >
                  {deletingProfile ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Deleting...
                    </span>
                  ) : (
                    "Delete Profile"
                  )}
                </button>
                <button
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                  disabled={deletingProfile}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSelector;
